import React, { useEffect, useState } from 'react';
import { adminApi, apiError } from '../../lib/adminApi';
import { toast } from 'sonner';
import { Send, Loader2, Mail, TestTube2, Plus } from 'lucide-react';

const TEMPLATES = [
  {
    id: 'promo',
    name: 'Offerta / Promo',
    subject: '🔥 Ειδική προσφορά S-MAN μόνο για εσάς',
    body: 'Γεια σας,\n\nΜόνο για λίγες ημέρες, απολαύστε αποκλειστική έκπτωση σε όλες τις γεύσεις S-MAN.\n\nΕνέργεια, αυτοπεποίθηση και απόδοση σε κάθε μέρα.',
    button_label: 'Αγοράστε τώρα',
  },
  {
    id: 'newproduct',
    name: 'Nuovo prodotto',
    subject: '🚀 Νέα γεύση S-MAN — δοκιμάστε την πρώτοι',
    body: 'Γεια σας,\n\nΜόλις κυκλοφόρησε μια ολοκαίνουρια γεύση S-MAN!\n\nΊδια φόρμουλα, νέα απόλαυση. Αποκτήστε τη πρώτοι.',
    button_label: 'Ανακαλύψτε τη',
  },
  {
    id: 'plain',
    name: 'Semplice',
    subject: '',
    body: '',
    button_label: '',
  },
];


const statusBadge = (s) => {
  const map = {
    draft: 'bg-white/10 text-white/60',
    sending: 'bg-yellow-500/20 text-yellow-300',
    sent: 'bg-green-500/20 text-green-300',
  };
  const label = { draft: 'Bozza', sending: 'In invio…', sent: 'Inviata' }[s] || s;
  return <span className={`text-xs px-2 py-1 rounded ${map[s] || 'bg-white/10'}`}>{label}</span>;
};

export default function CampaignsPanel() {
  const [regions, setRegions] = useState([]);
  const [totals, setTotals] = useState({});
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [region, setRegion] = useState('ALL');
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonUrl, setButtonUrl] = useState('https://the-sman.com');
  const [creating, setCreating] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const loadStats = () => adminApi.get('/admin/customers/stats').then((r) => {
    setRegions(r.data.by_region || []);
    setTotals({ ALL: r.data.subscribed, ...Object.fromEntries((r.data.by_region || []).map((x) => [x.region, x.count])) });
  }).catch(() => {});

  const loadCampaigns = () => adminApi.get('/admin/campaigns').then((r) => setCampaigns(r.data.items)).catch(() => {});

  useEffect(() => { loadStats(); loadCampaigns(); }, []);

  useEffect(() => {
    const sending = campaigns.some((c) => c.status === 'sending');
    if (!sending) return;
    const t = setInterval(loadCampaigns, 3000);
    return () => clearInterval(t);
  }, [campaigns]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    setButtonLabel(tpl.button_label || '');
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const html = body.replace(/\n/g, '<br>');
      await adminApi.post('/admin/campaigns', {
        subject, body: html, region,
        button_label: buttonLabel || null,
        button_url: buttonLabel ? buttonUrl : null,
      });
      toast.success('Campagna creata come bozza');
      setSubject(''); setBody(''); setButtonLabel('');
      loadCampaigns();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreating(false);
    }
  };

  const send = async (id) => {
    if (!window.confirm('Inviare questa campagna a tutti i clienti selezionati?')) return;
    setBusyId(id);
    try {
      const { data } = await adminApi.post(`/admin/campaigns/${id}/send`);
      toast.success(`Invio avviato a ${data.total_recipients} clienti`);
      loadCampaigns();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const testSend = async (id) => {
    const email = window.prompt('Invia un test a quale indirizzo email?');
    if (!email) return;
    setBusyId(id);
    try {
      await adminApi.post(`/admin/campaigns/${id}/test`, { email });
      toast.success('Email di test inviata');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="campaigns-panel">
      <form onSubmit={create} className="bg-[#0d0d0d] border border-white/10 rounded-xl p-6 space-y-4 h-fit">
        <h3 className="text-white font-bold flex items-center gap-2"><Plus size={16} className="text-red-500" /> Nuova campagna</h3>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Modello</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.id} type="button" data-testid={`template-${tpl.id}`}
                onClick={() => applyTemplate(tpl)}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-red-500 hover:text-white transition-colors"
              >{tpl.name}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Destinatari</label>
          <select
            data-testid="campaign-region-select"
            value={region} onChange={(e) => setRegion(e.target.value)}
            className="mt-2 w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-red-500"
          >
            <option value="ALL">Tutti gli iscritti ({totals.ALL ?? 0})</option>
            {regions.map((r) => <option key={r.region} value={r.region}>{r.region} ({r.count})</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Oggetto (θέμα)</label>
          <input
            data-testid="campaign-subject-input"
            value={subject} onChange={(e) => setSubject(e.target.value)} required
            className="mt-2 w-full bg-black border border-white/15 rounded-lg px-4 py-3 text-white outline-none focus:border-red-500"
            placeholder="π.χ. Νέα προσφορά S-MAN"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50">Messaggio (μήνυμα)</label>
          <textarea
            data-testid="campaign-body-input"
            value={body} onChange={(e) => setBody(e.target.value)} required rows={8}
            className="mt-2 w-full bg-black border border-white/15 rounded-lg px-4 py-3 text-white outline-none focus:border-red-500 resize-y"
            placeholder="Γράψτε εδώ το μήνυμά σας στα ελληνικά…"
          />
          <p className="text-white/30 text-xs mt-1">Il link di disiscrizione viene aggiunto automaticamente in fondo.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50">Pulsante (facolt.)</label>
            <input
              data-testid="campaign-button-label"
              value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)}
              className="mt-2 w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-red-500"
              placeholder="π.χ. Αγοράστε τώρα"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50">Link pulsante</label>
            <input
              data-testid="campaign-button-url"
              value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)}
              className="mt-2 w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-red-500"
              placeholder="https://the-sman.com"
            />
          </div>
        </div>
        <button
          data-testid="create-campaign-button"
          type="submit" disabled={creating}
          className="w-full bg-red-600 hover:bg-red-500 transition-colors text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {creating ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
          Crea bozza
        </button>
      </form>

      <div className="space-y-3">
        <h3 className="text-white font-bold">Campagne</h3>
        {campaigns.length === 0 && <p className="text-white/40 text-sm">Nessuna campagna ancora.</p>}
        {campaigns.map((c) => (
          <div key={c.id} className="bg-[#0d0d0d] border border-white/10 rounded-xl p-4" data-testid="campaign-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-semibold truncate">{c.subject}</p>
                <p className="text-white/40 text-xs mt-1">
                  {c.region === 'ALL' ? 'Tutti gli iscritti' : c.region} · {c.total_recipients} destinatari
                </p>
              </div>
              {statusBadge(c.status)}
            </div>
            {(c.status === 'sending' || c.status === 'sent') && (
              <p className="text-xs text-white/50 mt-2">
                Inviate: <span className="text-green-400">{c.sent_count}</span>
                {c.failed_count > 0 && <> · Fallite: <span className="text-red-400">{c.failed_count}</span></>}
                {' '}/ {c.total_recipients}
              </p>
            )}
            {c.status === 'draft' && (
              <div className="flex gap-2 mt-3">
                <button
                  data-testid="send-campaign-button"
                  onClick={() => send(c.id)} disabled={busyId === c.id}
                  className="flex-1 bg-red-600 hover:bg-red-500 text-white text-sm font-bold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busyId === c.id ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Invia
                </button>
                <button
                  data-testid="test-campaign-button"
                  onClick={() => testSend(c.id)} disabled={busyId === c.id}
                  className="px-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-sm rounded-lg flex items-center gap-2"
                >
                  <TestTube2 size={15} /> Test
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
